import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    translation: {
      live: {
        connected: 'connected',
        opponentDisconnected: 'Opponent disconnected',
        drawDeclined: 'Draw declined',
        connectionLost: 'Connection lost',
        illegalMove: 'Illegal move',
        moveError: 'Move error',
        opponentJoined: 'Opponent joined! The game has started',
        drawOfferSent: 'Draw offer sent',
        pgnExported: 'PGN exported',
        exportError: 'Export failed',
        opponent: 'Opponent',
        connecting: 'Connecting to the game...',
        yourTurn: 'Your move',
        opponentTurn: "Opponent's move",
        pot: 'Pot',
        waiting: 'Waiting for an opponent...',
        waitingHint: 'The game stays visible in the lobby until someone joins',
        draw: 'Draw!',
        victory: 'Victory!',
        defeat: 'Defeat',
        playAgain: 'Play again',
        white: 'White',
        black: 'Black',
        moves: 'Moves',
        noMoves: 'No moves yet',
        drawOffered: 'Draw offered',
        offerDraw: 'Offer draw',
        resign: 'Resign',
        resignConfirm: 'Are you sure you want to resign?',
        youLose: 'You will lose',
        drawOfferTitle: 'Draw offer',
        drawOfferText: 'Your opponent offers a draw. Accept?',
        decline: 'Decline',
        accept: 'Accept',
        reasons: {
          checkmate: 'Checkmate',
          resignation: 'Resignation',
          timeout: 'Time out',
          stalemate: 'Stalemate',
          agreement: 'By agreement',
          insufficient_material: 'Insufficient material',
          threefold_repetition: 'Threefold repetition',
          fifty_moves: 'Fifty-move rule',
        },
      },
      // Navigation
      nav: {
        home: 'Home',
        lobby: 'Lobby',
        tournaments: 'Tournaments',
        wallet: 'Wallet',
        profile: 'Profile',
        leaderboard: 'Leaderboard',
        referrals: 'Referrals',
        history: 'History',
        login: 'Login',
        register: 'Register',
        logout: 'Logout'
      },
      // Landing
      landing: {
        title: 'Play Chess',
        subtitle: 'Tournaments with Prize Pools',
        description: 'Join tournaments with players from around the world: every participant pays the same entry fee, and the prize pool goes to the top three finishers.',
        cta: 'Start Playing',
        features: {
          secure: 'Secure & Fair',
          secureDesc: 'Advanced anti-cheat AI ensures fair play for everyone',
          instant: 'Instant Payouts',
          instantDesc: 'Withdraw your winnings anytime with fast processing',
          rated: 'Rated Games',
          ratedDesc: 'ELO-based matchmaking for balanced competition'
        },
        stats: {
          players: 'bot levels, free play',
          games: 'online games & tournaments',
          prize: 'share the prize pool',
          top3: 'TOP-3'
        }
      },
      // Auth
      auth: {
        login: 'Login',
        register: 'Create Account',
        email: 'Email',
        password: 'Password',
        username: 'Username',
        confirmPassword: 'Confirm Password',
        forgotPassword: 'Forgot password?',
        noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?',
        loginBtn: 'Sign In',
        registerBtn: 'Create Account',
        referralApplied: 'Invite code {{code}} applied — you will get a welcome bonus!',
        orContinue: 'Or continue with'
      },
      blog: {
        feedTitle: 'News & lessons',
        all: 'All',
        write: 'Write',
        empty: 'No publications yet — new articles and videos appear here daily.',
        allCategories: 'All',
        loadMore: 'Load more',
        notFound: 'Publication not found',
        comments: 'Comments',
        commentPlaceholder: 'Write a comment…',
        send: 'Send',
        loginToComment: 'Sign in to comment',
        newPost: 'New publication',
        editPost: 'Edit publication',
        moderationNote: 'Your article will be published after a moderator review.',
        fields: { title: 'Title', excerpt: 'Short description', category: 'Category', lang: 'Language', content: 'Text', cover: 'Cover image', video: 'Video (YouTube / VK / Rutube link or file)', tags: 'Tags (comma separated)', publishAt: 'Publish at (optional, schedule)' },
        uploadImage: 'Upload image (up to 8 MB)',
        uploadVideo: 'or upload a video file (mp4/webm, up to 250 MB)',
        uploaded: 'Uploaded',
        fillRequired: 'Fill in the title and text',
        saveDraft: 'Save draft',
        publish: 'Publish',
        submitForReview: 'Submit for review',
        published: 'Published!',
        sentForReview: 'Sent for moderation',
        saved: 'Saved',
        status: { draft: 'Draft — visible only to you', pending: 'Awaiting moderation', rejected: 'Rejected by moderator' }
      },
      fairplay: {
        warnTitle: 'Fair-play notice',
        warnText: 'Our fair-play system noticed unusually engine-like moves in this game. Using chess engines, apps or any outside help is prohibited. Continued suspicious play leads to a frozen withdrawal and account review.',
        understood: 'I understand — I play fair',
        checked: 'Fair-play checked',
        checkedHint: 'This game was analysed by the fair-play engine',
        analyzing: 'Being checked'
      },
      ref: {
        title: 'Invite friends — earn together',
        subtitle: 'You receive {{percent}}% of every deposit your friends make. Forever.',
        yourLink: 'Your personal link',
        yourCode: 'Your code',
        howTitle: 'How it works',
        step1Title: 'Share the link',
        step1Text: 'Send your personal link to a friend. If they register through it, they are linked to you forever.',
        step2Title: 'Friend registers and gets a bonus',
        step2Text: 'A new player gets {{welcome}} Points on signup. If their first deposit is {{min}} Points or more, they get +{{bonus}} Points extra.',
        step3Title: 'You earn from every deposit',
        step3Text: 'Every time a friend tops up their wallet, {{percent}}% of the amount is credited to your referral balance. Example: friend deposits 1,000 ₽ → you get {{example}} Points.',
        step4Title: 'Transfer to your main balance',
        step4Text: 'Once your referral balance reaches {{min}} Points, press "Transfer" — the Points go to your main balance and can be used for stakes or withdrawal.',
        levelsTitle: 'Your percentage grows with the number of invited players',
        levelHint: 'Level depends on how many players registered through your link',
        tier1: 'up to 100 players',
        tier2: '101 – 1,000 players',
        tier3: 'over 1,000 players',
        individual: 'Individual rate agreed with TeaChess',
        levelBasic: 'Basic', levelGold: 'Gold', levelDiamond: 'Diamond', levelIndividual: 'Individual',
        currentLevel: 'Your level',
        invited: 'Invited players',
        toNext: 'more to next level',
        maxLevel: 'Maximum level reached',
        statReferrals: 'Invited',
        statActive: 'Made a deposit',
        statPending: 'Referral balance',
        statTotal: 'Earned all time',
        transfer: 'Transfer to balance',
        transferHint: 'Minimum {{min}} Points. Pending {{amount}} Points.',
        transferDone: '{{amount}} Points transferred to your balance',
        tabEarnings: 'Earnings',
        tabUsers: 'Your referrals',
        earningsTitle: 'Earnings history',
        noEarnings: 'No earnings yet — share your link!',
        deposited: 'deposited',
        paid: 'Transferred',
        pending: 'Pending',
        usersTitle: 'Players you invited',
        noUsers: 'No referrals yet',
        deposits: 'Deposits',
        active: 'Active',
        inactive: 'No deposits yet',
        copied: 'Link copied!',
        shareText: 'Play chess for money on TeaChess! Sign up with my link and get {{welcome}} Points to start.',
        faqTitle: 'Questions',
        faq1q: 'What counts as a referral?',
        faq1a: 'A player who registered through your link or entered your code. The link is attached to the account permanently.',
        faq2q: 'From what amounts do I earn?',
        faq2a: 'From every successful wallet top-up of your referral, with no limit on count or time. Points from game winnings and bonuses do not count.',
        faq3q: 'Can I withdraw referral earnings to a card?',
        faq3a: 'Yes. First transfer them to your main balance, then withdraw from the Wallet according to the general rules (KYC, minimum amount).',
        faq4q: 'Can I refer myself?',
        faq4a: 'No. Multi-accounting leads to cancellation of earnings and account blocking.'
      },
      // Game
      game: {
        viewingHistory: 'Viewing move',
        backToLive: 'back to game',
        levelNames: {1: 'Novice', 2: 'Beginner', 3: 'Amateur', 4: 'Club player', 5: 'Experienced', 6: 'Strong', 7: 'Candidate', 8: 'Master', 9: 'Grandmaster', 10: 'Super GM'},
        level: 'Level',
        createGame: 'Create Game',
        joinGame: 'Join Game',
        stake: 'Stake',
        timeControl: 'Time Control',
        increment: 'Increment',
        noStake: 'Free Game',
        waiting: 'Waiting for opponent...',
        yourTurn: 'Your Turn',
        opponentTurn: "Opponent's Turn",
        resign: 'Resign',
        draw: 'Offer Draw',
        rematch: 'Rematch',
        spectators: 'Spectators',
        moves: 'Moves',
        chat: 'Chat',
        gameOver: 'Game Over',
        youWin: 'You Win!',
        youLose: 'You Lose',
        drawResult: 'Draw',
        checkmate: 'Checkmate',
        stalemate: 'Stalemate',
        timeout: 'Timeout',
        resignation: 'Resignation',
        playWithBot: 'Play with Bot',
        selectDifficulty: 'Select Difficulty',
        difficulty: {
          easy: 'Easy',
          medium: 'Medium',
          hard: 'Hard',
          master: 'Master'
        },
        selectColor: 'Select Your Color',
        white: 'White',
        black: 'Black',
        startGame: 'Start Game',
        botOpponent: 'Bot',
        botThinking: 'Bot is thinking...',
        practiceMode: 'Practice Mode',
        moveHistory: 'Move History',
        noMoves: 'No moves yet',
        totalMoves: 'Total moves',
        pgnExported: 'PGN exported',
        exportPGN: 'Export PGN'
      },
      // Tournaments
      tournament: {
        title: 'Tournaments',
        upcoming: 'Upcoming',
        ongoing: 'Ongoing',
        finished: 'Finished',
        join: 'Join Tournament',
        entryFee: 'Entry Fee',
        prizePool: 'Prize Pool',
        players: 'Players',
        format: 'Format',
        swiss: 'Swiss',
        arena: 'Arena',
        rounds: 'Rounds',
        starts: 'Starts',
        registration: 'Registration Open',
        subtitle: 'Compete 24/7 in Arena and Swiss tournaments',
        botTitle: '24/7 tournaments with bots',
        botInfo: 'Play anytime! Bots fill tournaments for an instant start. Their level adapts to your rating.',
        botNotice: 'This tournament runs 24/7 with bots for an instant start. Bot level adapts to your rating.',
        live: 'LIVE',
        timeControl: 'Time control',
        start: 'Start',
        started: 'In progress',
        inMinutes: 'in {{count}} min',
        inHours: 'in {{count}} h',
        minutes: 'min',
        free: 'Free',
        joining: 'Joining...',
        full: 'Tournament full',
        noTournaments: 'No tournaments available',
        noTournamentsHint: 'Tournaments will appear soon. Refresh the page.',
        important: 'Important:',
        termsNotice: '24/7 tournaments include bots to keep games running. Details in the',
        termsLink: 'Terms of Service',
        registered: 'Registered',
        joined: 'You joined the tournament!',
        joinError: 'Could not join the tournament',
        connectionError: 'Connection error'
      },
      // Wallet
      wallet: {
        title: 'Wallet',
        balance: 'Balance',
        deposit: 'Deposit',
        withdraw: 'Withdraw',
        transactions: 'Transaction History',
        amount: 'Amount',
        cardNumber: 'Card Number',
        noTransactions: 'No transactions yet',
        depositSuccess: 'Deposit successful',
        withdrawSuccess: 'Withdrawal requested',
        insufficientBalance: 'Insufficient balance'
      },
      // Profile
      profile: {
        gameHistory: 'Game History',
        won: 'Won',
        draw: 'Draw',
        lost: 'Lost',
        title: 'Profile',
        rating: 'Rating',
        gamesPlayed: 'Games Played',
        gamesWon: 'Games Won',
        winRate: 'Win Rate',
        memberSince: 'Member Since',
        settings: 'Settings',
        language: 'Language',
        kyc: {
          title: 'Verification',
          notStarted: 'Not Verified',
          pending: 'Verification Pending',
          approved: 'Verified',
          required: 'Verification Required',
          startVerification: 'Start Verification',
          description: 'Identity verification is required before your first withdrawal'
        },
        botStats: {
          title: 'Bot Statistics',
          byDifficulty: 'By Difficulty',
          totalGames: 'Total Games',
          wins: 'Wins',
          losses: 'Losses',
          draws: 'Draws',
          winRate: 'Win Rate',
          currentStreak: 'Current Streak',
          bestStreak: 'Best Streak',
          recentGames: 'Recent Bot Games',
          noGames: 'No bot games yet',
          playNow: 'Play Now'
        }
      },
      // Common
      common: {
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        cancel: 'Cancel',
        confirm: 'Confirm',
        save: 'Save',
        delete: 'Delete',
        edit: 'Edit',
        back: 'Back',
        share: 'Share',
        next: 'Next',
        submit: 'Submit',
        search: 'Search',
        filter: 'Filter',
        noResults: 'No results found',
        minutes: 'min',
        seconds: 'sec',
        you: 'You',
        view: 'View',
        play: 'Play'
      },
      // Achievements
      achievements: {
        title: 'Achievements',
        description: 'Unlock achievements by playing games and improving your skills',
        points: 'Points',
        unlocked: 'Unlocked',
        progress: 'Progress',
        unlockedTitle: 'Unlocked',
        lockedTitle: 'Locked',
        noAchievements: 'No achievements in this category',
        viewAll: 'View All Achievements',
        categories: {
          all: 'All',
          bot: 'Bot',
          pvp: 'PvP',
          rating: 'Rating',
          special: 'Special',
          account: 'Account'
        }
      },
      // Friends
      friends: {
        title: 'Friends',
        description: 'Add friends and challenge them to games',
        addFriend: 'Add Friend',
        searchPlaceholder: 'Enter username...',
        myFriends: 'My Friends',
        requests: 'Requests',
        challenges: 'Challenges',
        noFriends: 'No friends yet',
        noIncoming: 'No incoming requests',
        noOutgoing: 'No outgoing requests',
        noChallenges: 'No challenges',
        incoming: 'Incoming',
        outgoing: 'Outgoing',
        incomingChallenges: 'Incoming Challenges',
        outgoingChallenges: 'Outgoing Challenges',
        wantsToBeYourFriend: 'wants to be your friend',
        pendingRequest: 'Request pending',
        waiting: 'Waiting...',
        challenge: 'Challenge',
        accept: 'Accept',
        decline: 'Decline',
        removed: 'Friend removed',
        challengePlayer: 'Challenge Player',
        challengeDescription: 'Send a game challenge to {{name}}',
        sendChallenge: 'Send Challenge',
        stakeNote: 'Leave 0 for free game. KYC required for stakes.'
      },
      // Settings
      settings: {
        title: 'Board Settings',
        description: 'Customize your chess board appearance',
        boardTheme: 'Board Theme',
        pieceStyle: 'Piece Style',
        preview: 'Live Preview'
      },
      // KYC
      kyc: {
        mockMode: 'Sumsub not configured. Using mock verification.',
        submitted: 'Verification submitted. We will review your documents.',
        resubmitted: 'Documents resubmitted for review.',
        approved: 'Verification approved! You can now play for real money.',
        rejected: 'Verification rejected. Please try again.',
        retry: 'Additional documents required',
        error: 'Verification error. Please try again.',
        verifiedTitle: 'Identity Verified',
        verifiedDescription: 'You can play games with real money stakes.',
        pendingTitle: 'Verification In Progress',
        pendingDescription: 'Your documents are being reviewed. This usually takes a few minutes.',
        rejectedTitle: 'Verification Failed',
        rejectedDescription: 'Please try again with valid documents.',
        retryTitle: 'Additional Information Required',
        retryVerification: 'Retry Verification',
        approveMock: 'Approve (Test Mode)',
        whyTitle: 'Why verify?',
        whyText: 'Playing and depositing need no verification. To withdraw winnings to a card we are required to confirm your identity once — it takes 2–3 minutes.',
        chooseMethod: 'Choose a verification method',
        diditTitle: 'Passport or ID + selfie',
        diditDesc: 'Photograph your document and take a selfie. Any country. Result usually within a minute.',
        mtsTitle: 'Via MTS ID',
        mtsDesc: 'Sign in with your MTS account — name, date of birth and phone are confirmed by the operator. For Russian residents.',
        start: 'Start',
        notConfigured: 'Temporarily unavailable',
        testMode: 'Test mode: the provider is not connected yet',
        verifiedVia: 'Verified via',
        returned: 'Checking the result…',
        cancelled: 'Verification was cancelled. You can try again.',
        incomplete: 'MTS ID did not return your name. Please use document verification.',
        tryAgain: 'Try again'
      },
      // Notifications
      notifications: {
        kycApproved: 'Identity verification approved!',
        kycRejected: 'Identity verification rejected.',
        newChallenge: 'New game challenge!',
        challengeAccepted: 'Challenge accepted! Game starting...'
      }
    }
  },
  ru: {
    translation: {
      live: {
        connected: 'подключился',
        opponentDisconnected: 'Соперник отключился',
        drawDeclined: 'Ничья отклонена',
        connectionLost: 'Соединение потеряно',
        illegalMove: 'Недопустимый ход',
        moveError: 'Ошибка хода',
        opponentJoined: 'Соперник присоединился! Игра началась',
        drawOfferSent: 'Предложение ничьей отправлено',
        pgnExported: 'PGN экспортирован',
        exportError: 'Ошибка экспорта',
        opponent: 'Соперник',
        connecting: 'Подключение к игре...',
        yourTurn: 'Ваш ход',
        opponentTurn: 'Ход соперника',
        pot: 'На кону',
        waiting: 'Ожидание соперника...',
        waitingHint: 'Партия видна в лобби, пока кто-нибудь не присоединится',
        draw: 'Ничья!',
        victory: 'Победа!',
        defeat: 'Поражение',
        playAgain: 'Играть снова',
        white: 'Белые',
        black: 'Чёрные',
        moves: 'Ходы',
        noMoves: 'Ещё нет ходов',
        drawOffered: 'Ничья предложена',
        offerDraw: 'Предложить ничью',
        resign: 'Сдаться',
        resignConfirm: 'Вы уверены, что хотите сдаться?',
        youLose: 'Вы потеряете',
        drawOfferTitle: 'Предложение ничьей',
        drawOfferText: 'Соперник предлагает ничью. Принять?',
        decline: 'Отклонить',
        accept: 'Принять',
        reasons: {
          checkmate: 'Мат',
          resignation: 'Сдача',
          timeout: 'Время вышло',
          stalemate: 'Пат',
          agreement: 'По согласию',
          insufficient_material: 'Недостаточно материала',
          threefold_repetition: 'Троекратное повторение',
          fifty_moves: 'Правило 50 ходов',
        },
      },
      // Navigation
      nav: {
        home: 'Главная',
        lobby: 'Лобби',
        tournaments: 'Турниры',
        wallet: 'Кошелек',
        profile: 'Профиль',
        leaderboard: 'Рейтинг',
        referrals: 'Рефералы',
        history: 'История',
        login: 'Вход',
        register: 'Регистрация',
        logout: 'Выход'
      },
      // Landing
      landing: {
        title: 'Играй в шахматы',
        subtitle: 'Турниры с призовым фондом',
        description: 'Участвуйте в турнирах с игроками со всего мира: все вносят одинаковый взнос, а призовой фонд делят победитель и призёры — 1, 2 и 3 места.',
        cta: 'Начать игру',
        features: {
          secure: 'Безопасно и честно',
          secureDesc: 'Продвинутый анти-чит ИИ обеспечивает честную игру',
          instant: 'Мгновенные выплаты',
          instantDesc: 'Выводите выигрыш в любое время с быстрой обработкой',
          rated: 'Рейтинговые игры',
          ratedDesc: 'Подбор по ELO для сбалансированной конкуренции'
        },
        stats: {
          players: 'уровней бота — играйте бесплатно',
          games: 'онлайн-партии и турниры',
          prize: 'делят призовой фонд',
          top3: 'ТОП-3'
        }
      },
      // Auth
      auth: {
        login: 'Вход',
        register: 'Регистрация',
        email: 'Email',
        password: 'Пароль',
        username: 'Имя пользователя',
        confirmPassword: 'Подтвердите пароль',
        forgotPassword: 'Забыли пароль?',
        noAccount: 'Нет аккаунта?',
        hasAccount: 'Уже есть аккаунт?',
        loginBtn: 'Войти',
        registerBtn: 'Создать аккаунт',
        referralApplied: 'Код приглашения {{code}} применён — вы получите приветственный бонус!',
        orContinue: 'Или продолжить с'
      },
      blog: {
        feedTitle: 'Новости и обучение',
        all: 'Все',
        write: 'Написать',
        empty: 'Публикаций пока нет — новые статьи и видео появляются здесь ежедневно.',
        allCategories: 'Все',
        loadMore: 'Показать ещё',
        notFound: 'Публикация не найдена',
        comments: 'Комментарии',
        commentPlaceholder: 'Написать комментарий…',
        send: 'Отправить',
        loginToComment: 'Войдите, чтобы комментировать',
        newPost: 'Новая публикация',
        editPost: 'Редактировать публикацию',
        moderationNote: 'Ваша статья появится на сайте после проверки модератором.',
        fields: { title: 'Заголовок', excerpt: 'Короткое описание', category: 'Категория', lang: 'Язык', content: 'Текст', cover: 'Обложка', video: 'Видео (ссылка YouTube / VK / Rutube или файл)', tags: 'Теги (через запятую)', publishAt: 'Дата публикации (необязательно, отложенный выход)' },
        uploadImage: 'Загрузить изображение (до 8 МБ)',
        uploadVideo: 'или загрузить видеофайл (mp4/webm, до 250 МБ)',
        uploaded: 'Загружено',
        fillRequired: 'Заполните заголовок и текст',
        saveDraft: 'В черновики',
        publish: 'Опубликовать',
        submitForReview: 'Отправить на модерацию',
        published: 'Опубликовано!',
        sentForReview: 'Отправлено на модерацию',
        saved: 'Сохранено',
        status: { draft: 'Черновик — видно только вам', pending: 'Ожидает модерации', rejected: 'Отклонено модератором' }
      },
      fairplay: {
        warnTitle: 'Предупреждение системы честной игры',
        warnText: 'Система заметила в этой партии необычно точные, «компьютерные» ходы. Использование шахматных движков, приложений и любых подсказок запрещено. При продолжении подозрительной игры вывод средств будет заморожен, а аккаунт отправлен на проверку.',
        understood: 'Понятно — играю честно',
        checked: 'Партия проверена',
        checkedHint: 'Партия проанализирована системой честной игры',
        analyzing: 'Проверяется'
      },
      ref: {
        title: 'Приглашай друзей — зарабатывай вместе',
        subtitle: 'Вы получаете {{percent}}% с каждого пополнения кошелька ваших друзей. Навсегда.',
        yourLink: 'Ваша персональная ссылка',
        yourCode: 'Ваш код',
        howTitle: 'Как это работает',
        step1Title: 'Поделитесь ссылкой',
        step1Text: 'Отправьте другу свою персональную ссылку. Если он зарегистрируется по ней, он навсегда закрепляется за вами.',
        step2Title: 'Друг регистрируется и получает бонус',
        step2Text: 'Новый игрок получает {{welcome}} Points при регистрации. Если его первое пополнение — от {{min}} Points, он получит ещё +{{bonus}} Points.',
        step3Title: 'Вы зарабатываете с каждого пополнения',
        step3Text: 'Каждый раз, когда друг пополняет кошелёк, {{percent}}% от суммы зачисляется на ваш реферальный баланс. Пример: друг пополнил на 1 000 ₽ → вы получили {{example}} Points.',
        step4Title: 'Переведите на основной баланс',
        step4Text: 'Когда реферальный баланс достигнет {{min}} Points, нажмите «Перевести» — Points попадут на основной баланс, их можно ставить в играх или выводить.',
        levelsTitle: 'Ваш процент растёт с числом приглашённых',
        levelHint: 'Уровень зависит от того, сколько игроков зарегистрировалось по вашей ссылке',
        tier1: 'до 100 игроков',
        tier2: '101 – 1 000 игроков',
        tier3: 'более 1 000 игроков',
        individual: 'Индивидуальная ставка по договорённости с TeaChess',
        levelBasic: 'Базовый', levelGold: 'Золотой', levelDiamond: 'Бриллиантовый', levelIndividual: 'Индивидуальный',
        currentLevel: 'Ваш уровень',
        invited: 'Приглашено игроков',
        toNext: 'до следующего уровня',
        maxLevel: 'Достигнут максимальный уровень',
        statReferrals: 'Приглашено',
        statActive: 'Сделали депозит',
        statPending: 'Реферальный баланс',
        statTotal: 'Заработано за всё время',
        transfer: 'Перевести на баланс',
        transferHint: 'Минимум {{min}} Points. Накоплено {{amount}} Points.',
        transferDone: '{{amount}} Points переведены на основной баланс',
        tabEarnings: 'Начисления',
        tabUsers: 'Мои рефералы',
        earningsTitle: 'История начислений',
        noEarnings: 'Начислений пока нет — поделитесь ссылкой!',
        deposited: 'пополнил на',
        paid: 'Переведено',
        pending: 'Ожидает',
        usersTitle: 'Приглашённые игроки',
        noUsers: 'Рефералов пока нет',
        deposits: 'Пополнения',
        active: 'Активен',
        inactive: 'Ещё не пополнял',
        copied: 'Ссылка скопирована!',
        shareText: 'Играй в шахматы на деньги в TeaChess! Регистрируйся по моей ссылке и получи {{welcome}} Points на старт.',
        faqTitle: 'Вопросы',
        faq1q: 'Кто считается рефералом?',
        faq1a: 'Игрок, который зарегистрировался по вашей ссылке или ввёл ваш код. Привязка к вам сохраняется навсегда.',
        faq2q: 'С каких сумм я получаю процент?',
        faq2a: 'С каждого успешного пополнения кошелька вашего реферала, без ограничений по количеству и сроку. Выигрыши и бонусы реферала не учитываются.',
        faq3q: 'Можно ли вывести реферальный доход на карту?',
        faq3a: 'Да. Сначала переведите его на основной баланс, затем выводите через Кошелёк по общим правилам (KYC, минимальная сумма).',
        faq4q: 'Можно ли пригласить самого себя?',
        faq4a: 'Нет. Мультиаккаунты ведут к аннулированию начислений и блокировке аккаунта.'
      },
      // Game
      game: {
        viewingHistory: 'Просмотр хода',
        backToLive: 'к партии',
        levelNames: {1: 'Новичок', 2: 'Начинающий', 3: 'Любитель', 4: 'Игрок клуба', 5: 'Опытный', 6: 'Сильный', 7: 'Кандидат', 8: 'Мастер', 9: 'Гроссмейстер', 10: 'Сверхгроссмейстер'},
        level: 'Уровень',
        createGame: 'Создать игру',
        joinGame: 'Присоединиться',
        stake: 'Ставка',
        timeControl: 'Контроль времени',
        increment: 'Добавка',
        noStake: 'Бесплатная игра',
        waiting: 'Ожидание соперника...',
        yourTurn: 'Ваш ход',
        opponentTurn: 'Ход соперника',
        resign: 'Сдаться',
        draw: 'Предложить ничью',
        rematch: 'Реванш',
        spectators: 'Зрители',
        moves: 'Ходы',
        chat: 'Чат',
        gameOver: 'Игра окончена',
        youWin: 'Вы победили!',
        youLose: 'Вы проиграли',
        drawResult: 'Ничья',
        checkmate: 'Мат',
        stalemate: 'Пат',
        timeout: 'Время вышло',
        resignation: 'Сдача',
        playWithBot: 'Играть с ботом',
        selectDifficulty: 'Выберите сложность',
        difficulty: {
          easy: 'Легкий',
          medium: 'Средний',
          hard: 'Сложный',
          master: 'Мастер'
        },
        selectColor: 'Выберите цвет',
        white: 'Белые',
        black: 'Черные',
        startGame: 'Начать игру',
        botOpponent: 'Бот',
        botThinking: 'Бот думает...',
        practiceMode: 'Тренировка',
        moveHistory: 'История ходов',
        noMoves: 'Ходов пока нет',
        totalMoves: 'Всего ходов',
        pgnExported: 'PGN экспортирован',
        exportPGN: 'Экспорт PGN'
      },
      // Tournaments
      tournament: {
        title: 'Турниры',
        upcoming: 'Предстоящие',
        ongoing: 'Активные',
        finished: 'Завершенные',
        join: 'Участвовать',
        entryFee: 'Взнос',
        prizePool: 'Призовой фонд',
        players: 'Игроки',
        format: 'Формат',
        swiss: 'Швейцарская',
        arena: 'Арена',
        rounds: 'Раунды',
        starts: 'Начало',
        registration: 'Регистрация открыта',
        subtitle: 'Соревнуйтесь 24/7 в Arena и Swiss турнирах',
        botTitle: 'Турниры 24/7 с ботами',
        botInfo: 'Играйте в любое время! Боты заполняют турниры для мгновенного старта. Их уровень адаптируется под ваш рейтинг.',
        botNotice: 'Турнир работает 24/7 с ботами для мгновенного старта. Уровень ботов адаптируется под ваш рейтинг.',
        live: 'LIVE',
        timeControl: 'Контроль',
        start: 'Старт',
        started: 'Идёт',
        inMinutes: 'через {{count}} мин',
        inHours: 'через {{count}} ч',
        minutes: 'мин',
        free: 'Бесплатно',
        joining: 'Присоединение...',
        full: 'Турнир заполнен',
        noTournaments: 'Нет доступных турниров',
        noTournamentsHint: 'Турниры скоро появятся. Обновите страницу.',
        important: 'Важно:',
        termsNotice: 'Турниры 24/7 включают ботов для обеспечения непрерывной игры. Подробности в',
        termsLink: 'Условиях использования',
        registered: 'Вы участвуете',
        joined: 'Вы присоединились к турниру!',
        joinError: 'Не удалось присоединиться',
        connectionError: 'Ошибка соединения'
      },
      // Wallet
      wallet: {
        title: 'Кошелек',
        balance: 'Баланс',
        deposit: 'Пополнить',
        withdraw: 'Вывести',
        transactions: 'История транзакций',
        amount: 'Сумма',
        cardNumber: 'Номер карты',
        noTransactions: 'Транзакций пока нет',
        depositSuccess: 'Пополнение успешно',
        withdrawSuccess: 'Заявка на вывод отправлена',
        insufficientBalance: 'Недостаточно средств'
      },
      // Profile
      profile: {
        gameHistory: 'История игр',
        won: 'Победа',
        draw: 'Ничья',
        lost: 'Поражение',
        title: 'Профиль',
        rating: 'Рейтинг',
        gamesPlayed: 'Сыграно партий',
        gamesWon: 'Побед',
        winRate: 'Процент побед',
        memberSince: 'Участник с',
        settings: 'Настройки',
        language: 'Язык',
        kyc: {
          title: 'Верификация',
          notStarted: 'Не верифицирован',
          pending: 'На проверке',
          approved: 'Верифицирован',
          required: 'Требуется верификация',
          startVerification: 'Пройти верификацию',
          description: 'Верификация личности нужна перед первым выводом средств'
        },
        botStats: {
          title: 'Статистика с ботом',
          byDifficulty: 'По сложности',
          totalGames: 'Всего игр',
          wins: 'Победы',
          losses: 'Поражения',
          draws: 'Ничьи',
          winRate: 'Процент побед',
          currentStreak: 'Текущая серия',
          bestStreak: 'Лучшая серия',
          recentGames: 'Последние игры с ботом',
          noGames: 'Нет игр с ботом',
          playNow: 'Играть'
        }
      },
      // Common
      common: {
        loading: 'Загрузка...',
        error: 'Ошибка',
        success: 'Успешно',
        cancel: 'Отмена',
        confirm: 'Подтвердить',
        save: 'Сохранить',
        delete: 'Удалить',
        edit: 'Редактировать',
        back: 'Назад',
        share: 'Поделиться',
        next: 'Далее',
        submit: 'Отправить',
        search: 'Поиск',
        filter: 'Фильтр',
        noResults: 'Ничего не найдено',
        minutes: 'мин',
        seconds: 'сек',
        you: 'Вы',
        view: 'Смотреть',
        play: 'Играть'
      },
      // Achievements
      achievements: {
        title: 'Достижения',
        description: 'Открывайте достижения, играя и улучшая свои навыки',
        points: 'Очки',
        unlocked: 'Получено',
        progress: 'Прогресс',
        unlockedTitle: 'Открытые',
        lockedTitle: 'Закрытые',
        noAchievements: 'Нет достижений в этой категории',
        viewAll: 'Все достижения',
        categories: {
          all: 'Все',
          bot: 'Бот',
          pvp: 'PvP',
          rating: 'Рейтинг',
          special: 'Особые',
          account: 'Аккаунт'
        }
      },
      // Friends
      friends: {
        title: 'Друзья',
        description: 'Добавляйте друзей и вызывайте их на игру',
        addFriend: 'Добавить друга',
        searchPlaceholder: 'Введите имя пользователя...',
        myFriends: 'Мои друзья',
        requests: 'Заявки',
        challenges: 'Вызовы',
        noFriends: 'Пока нет друзей',
        noIncoming: 'Нет входящих заявок',
        noOutgoing: 'Нет исходящих заявок',
        noChallenges: 'Нет вызовов',
        incoming: 'Входящие',
        outgoing: 'Исходящие',
        incomingChallenges: 'Входящие вызовы',
        outgoingChallenges: 'Исходящие вызовы',
        wantsToBeYourFriend: 'хочет добавить вас в друзья',
        pendingRequest: 'Заявка отправлена',
        waiting: 'Ожидание...',
        challenge: 'Вызвать',
        accept: 'Принять',
        decline: 'Отклонить',
        removed: 'Друг удален',
        challengePlayer: 'Вызов игрока',
        challengeDescription: 'Отправить вызов {{name}}',
        sendChallenge: 'Отправить вызов',
        stakeNote: 'Оставьте 0 для бесплатной игры. Для ставок нужна верификация.'
      },
      // Settings
      settings: {
        title: 'Настройки доски',
        description: 'Настройте внешний вид шахматной доски',
        boardTheme: 'Тема доски',
        pieceStyle: 'Стиль фигур',
        preview: 'Предпросмотр'
      },
      // KYC
      kyc: {
        mockMode: 'Sumsub не настроен. Используется тестовая верификация.',
        submitted: 'Верификация отправлена. Мы проверим ваши документы.',
        resubmitted: 'Документы отправлены повторно на проверку.',
        approved: 'Верификация пройдена! Теперь вы можете играть на деньги.',
        rejected: 'Верификация отклонена. Попробуйте снова.',
        retry: 'Требуются дополнительные документы',
        error: 'Ошибка верификации. Попробуйте снова.',
        verifiedTitle: 'Личность подтверждена',
        verifiedDescription: 'Вы можете играть на реальные деньги.',
        pendingTitle: 'Верификация в процессе',
        pendingDescription: 'Ваши документы проверяются. Обычно это занимает несколько минут.',
        rejectedTitle: 'Верификация не пройдена',
        rejectedDescription: 'Попробуйте снова с действительными документами.',
        retryTitle: 'Требуется дополнительная информация',
        retryVerification: 'Повторить верификацию',
        approveMock: 'Одобрить (тестовый режим)',
        whyTitle: 'Зачем нужна верификация?',
        whyText: 'Играть и пополнять можно без проверки. Чтобы вывести выигрыш на карту, мы обязаны один раз подтвердить вашу личность — это занимает 2–3 минуты.',
        chooseMethod: 'Выберите способ верификации',
        diditTitle: 'Паспорт или ID + селфи',
        diditDesc: 'Сфотографируйте документ и сделайте селфи. Любая страна. Результат обычно в течение минуты.',
        mtsTitle: 'Через МТС ID',
        mtsDesc: 'Войдите через аккаунт МТС — ФИО, дату рождения и телефон подтверждает оператор. Для жителей России.',
        start: 'Начать',
        notConfigured: 'Временно недоступно',
        testMode: 'Тестовый режим: провайдер ещё не подключён',
        verifiedVia: 'Подтверждено через',
        returned: 'Проверяем результат…',
        cancelled: 'Верификация отменена. Можно попробовать снова.',
        incomplete: 'МТС ID не передал ФИО. Пройдите верификацию по документу.',
        tryAgain: 'Попробовать снова'
      },
      // Notifications
      notifications: {
        kycApproved: 'Верификация личности пройдена!',
        kycRejected: 'Верификация личности отклонена.',
        newChallenge: 'Новый вызов на игру!',
        challengeAccepted: 'Вызов принят! Игра начинается...'
      }
    }
  },
  // Chinese translations
  zh: {
    translation: {
      live: {
        connected: '已连接',
        opponentDisconnected: '对手已断开',
        drawDeclined: '和棋被拒绝',
        connectionLost: '连接丢失',
        illegalMove: '非法走法',
        moveError: '走法错误',
        opponentJoined: '对手已加入！对局开始',
        drawOfferSent: '已发送和棋提议',
        pgnExported: 'PGN 已导出',
        exportError: '导出失败',
        opponent: '对手',
        connecting: '正在连接对局...',
        yourTurn: '轮到您',
        opponentTurn: '对手回合',
        pot: '奖池',
        waiting: '等待对手...',
        waitingHint: '在有人加入之前，对局会显示在大厅中',
        draw: '和棋！',
        victory: '胜利！',
        defeat: '失败',
        playAgain: '再来一局',
        white: '白方',
        black: '黑方',
        moves: '着法',
        noMoves: '暂无着法',
        drawOffered: '已提议和棋',
        offerDraw: '提议和棋',
        resign: '认输',
        resignConfirm: '确定要认输吗？',
        youLose: '您将失去',
        drawOfferTitle: '和棋提议',
        drawOfferText: '对手提议和棋。接受吗？',
        decline: '拒绝',
        accept: '接受',
        reasons: {
          checkmate: '将死',
          resignation: '认输',
          timeout: '超时',
          stalemate: '逼和',
          agreement: '协议和棋',
          insufficient_material: '子力不足',
          threefold_repetition: '三次重复',
          fifty_moves: '五十步规则',
        },
      },
      // Navigation
      nav: {
        home: '首页',
        lobby: '大厅',
        tournaments: '锦标赛',
        wallet: '钱包',
        profile: '个人资料',
        leaderboard: '排行榜',
        referrals: '推荐',
        history: '历史',
        login: '登录',
        register: '注册',
        logout: '退出'
      },
      // Landing
      landing: {
        title: '象棋对战',
        subtitle: '奖金池锦标赛',
        description: '与全球玩家一起参加锦标赛：所有参赛者缴纳相同的报名费，奖金池由前三名瓜分。',
        cta: '开始游戏',
        features: {
          secure: '安全公平',
          secureDesc: '先进的反作弊AI确保公平竞技',
          instant: '即时提现',
          instantDesc: '随时提取您的奖金，快速处理',
          rated: '积分对战',
          ratedDesc: 'ELO评分系统确保公平匹配'
        },
        stats: {
          players: '级人机难度，免费畅玩',
          games: '在线对局与锦标赛',
          prize: '瓜分奖金池',
          top3: '前三名'
        }
      },
      // Auth
      auth: {
        login: '登录',
        register: '创建账户',
        email: '邮箱',
        password: '密码',
        username: '用户名',
        confirmPassword: '确认密码',
        forgotPassword: '忘记密码？',
        noAccount: '没有账户？',
        hasAccount: '已有账户？',
        loginBtn: '登录',
        registerBtn: '注册',
        referralApplied: '邀请码 {{code}} 已应用 — 您将获得欢迎奖励！',
        orContinue: '或继续使用'
      },
      blog: {
        feedTitle: '新闻与教学',
        all: '全部',
        write: '撰写',
        empty: '暂无内容 — 新文章和视频每天在此更新。',
        allCategories: '全部',
        loadMore: '加载更多',
        notFound: '未找到文章',
        comments: '评论',
        commentPlaceholder: '写评论…',
        send: '发送',
        loginToComment: '登录后评论',
        newPost: '新文章',
        editPost: '编辑文章',
        moderationNote: '您的文章将在审核通过后发布。',
        fields: { title: '标题', excerpt: '简介', category: '分类', lang: '语言', content: '正文', cover: '封面图', video: '视频（YouTube / VK / Rutube 链接或文件）', tags: '标签（逗号分隔）', publishAt: '发布时间（可选，定时发布）' },
        uploadImage: '上传图片（最大 8 MB）',
        uploadVideo: '或上传视频文件（mp4/webm，最大 250 MB）',
        uploaded: '已上传',
        fillRequired: '请填写标题和正文',
        saveDraft: '存为草稿',
        publish: '发布',
        submitForReview: '提交审核',
        published: '已发布！',
        sentForReview: '已提交审核',
        saved: '已保存',
        status: { draft: '草稿 — 仅您可见', pending: '等待审核', rejected: '已被审核员拒绝' }
      },
      fairplay: {
        warnTitle: '公平竞赛提醒',
        warnText: '系统在本局中发现异常精准的"引擎式"走法。禁止使用棋类引擎、应用或任何外部帮助。若继续可疑行为，提现将被冻结并对账户进行审查。',
        understood: '明白 — 我会公平竞赛',
        checked: '已通过公平检查',
        checkedHint: '本局已由公平竞赛引擎分析',
        analyzing: '检查中'
      },
      ref: {
        title: '邀请好友 — 一起赚取',
        subtitle: '好友每次充值，您都可获得 {{percent}}%。永久有效。',
        yourLink: '您的专属链接',
        yourCode: '您的邀请码',
        howTitle: '运作方式',
        step1Title: '分享链接',
        step1Text: '把专属链接发给好友。好友通过该链接注册后，将永久与您绑定。',
        step2Title: '好友注册并获得奖励',
        step2Text: '新玩家注册即得 {{welcome}} Points。若首次充值不少于 {{min}} Points，还可额外获得 +{{bonus}} Points。',
        step3Title: '好友每次充值您都有收益',
        step3Text: '好友每次充值钱包，金额的 {{percent}}% 将计入您的推荐余额。例如：好友充值 1,000 ₽ → 您获得 {{example}} Points。',
        step4Title: '转入主余额',
        step4Text: '推荐余额达到 {{min}} Points 后，点击"转入"— Points 将进入主余额，可用于对局下注或提现。',
        levelsTitle: '邀请人数越多，比例越高',
        levelHint: '等级取决于通过您的链接注册的玩家数量',
        tier1: '最多 100 名玩家',
        tier2: '101 – 1,000 名玩家',
        tier3: '超过 1,000 名玩家',
        individual: '与 TeaChess 协商的个人比例',
        levelBasic: '基础', levelGold: '黄金', levelDiamond: '钻石', levelIndividual: '个人',
        currentLevel: '您的等级',
        invited: '已邀请玩家',
        toNext: '距下一等级',
        maxLevel: '已达最高等级',
        statReferrals: '已邀请',
        statActive: '已充值',
        statPending: '推荐余额',
        statTotal: '累计收益',
        transfer: '转入主余额',
        transferHint: '最低 {{min}} Points。已累计 {{amount}} Points。',
        transferDone: '{{amount}} Points 已转入主余额',
        tabEarnings: '收益',
        tabUsers: '我的推荐',
        earningsTitle: '收益记录',
        noEarnings: '暂无收益 — 快分享链接吧！',
        deposited: '充值了',
        paid: '已转入',
        pending: '待转入',
        usersTitle: '您邀请的玩家',
        noUsers: '暂无推荐用户',
        deposits: '充值',
        active: '活跃',
        inactive: '尚未充值',
        copied: '链接已复制！',
        shareText: '在 TeaChess 下真钱象棋！通过我的链接注册即可获得 {{welcome}} Points 起步奖励。',
        faqTitle: '常见问题',
        faq1q: '谁算作推荐用户？',
        faq1a: '通过您的链接注册或输入您邀请码的玩家。绑定关系永久有效。',
        faq2q: '哪些金额可以计提？',
        faq2a: '推荐用户每一次成功的钱包充值，次数和时间不限。对局赢取和奖励不计入。',
        faq3q: '推荐收益能提现到银行卡吗？',
        faq3a: '可以。先转入主余额，再按一般规则（KYC、最低金额）在钱包中提现。',
        faq4q: '可以推荐自己吗？',
        faq4a: '不可以。多账户行为将导致收益作废并封禁账户。'
      },
      // Game
      game: {
        viewingHistory: '查看着法',
        backToLive: '返回对局',
        levelNames: {1: '新手', 2: '初学者', 3: '业余', 4: '俱乐部棋手', 5: '有经验', 6: '强手', 7: '候选人', 8: '大师', 9: '特级大师', 10: '超级特级大师'},
        level: '等级',
        createGame: '创建对局',
        joinGame: '加入对局',
        stake: '赌注',
        timeControl: '时间控制',
        increment: '每步加时',
        noStake: '免费对局',
        waiting: '等待对手...',
        yourTurn: '轮到你了',
        opponentTurn: '对手回合',
        resign: '认输',
        draw: '求和',
        rematch: '再来一局',
        spectators: '观众',
        moves: '走法',
        chat: '聊天',
        gameOver: '对局结束',
        youWin: '你赢了！',
        youLose: '你输了',
        drawResult: '和棋',
        checkmate: '将死',
        stalemate: '逼和',
        timeout: '超时',
        resignation: '认输',
        playWithBot: '人机对战',
        selectDifficulty: '选择难度',
        difficulty: {
          easy: '简单',
          medium: '中等',
          hard: '困难',
          master: '大师'
        },
        selectColor: '选择颜色',
        white: '白方',
        black: '黑方',
        startGame: '开始对局',
        botOpponent: '电脑',
        botThinking: '电脑思考中...',
        practiceMode: '练习模式',
        moveHistory: '走法记录',
        noMoves: '暂无走法',
        totalMoves: '总步数',
        pgnExported: 'PGN已导出',
        exportPGN: '导出PGN'
      },
      // Tournaments
      tournament: {
        title: '锦标赛',
        upcoming: '即将开始',
        ongoing: '进行中',
        finished: '已结束',
        join: '参加比赛',
        entryFee: '报名费',
        prizePool: '奖池',
        players: '参赛者',
        format: '赛制',
        swiss: '瑞士制',
        arena: '竞技场',
        rounds: '轮次',
        starts: '开始时间',
        registration: '报名开放',
        subtitle: '全天候参加 Arena 和瑞士制锦标赛',
        botTitle: '24/7 机器人锦标赛',
        botInfo: '随时参赛！机器人填满锦标赛以便即时开始，其水平会根据您的等级分调整。',
        botNotice: '本锦标赛全天候运行，机器人即时开赛，水平根据您的等级分调整。',
        live: '进行中',
        timeControl: '时间控制',
        start: '开始',
        started: '进行中',
        inMinutes: '{{count}} 分钟后',
        inHours: '{{count}} 小时后',
        minutes: '分钟',
        free: '免费',
        joining: '加入中...',
        full: '锦标赛已满',
        noTournaments: '暂无可用锦标赛',
        noTournamentsHint: '锦标赛即将推出，请刷新页面。',
        important: '重要：',
        termsNotice: '24/7 锦标赛包含机器人以保证持续对局。详情见',
        termsLink: '服务条款',
        registered: '已报名',
        joined: '您已加入锦标赛！',
        joinError: '无法加入锦标赛',
        connectionError: '连接错误'
      },
      // Wallet
      wallet: {
        title: '钱包',
        balance: '余额',
        deposit: '充值',
        withdraw: '提现',
        transactions: '交易记录',
        amount: '金额',
        cardNumber: '卡号',
        noTransactions: '暂无交易记录',
        depositSuccess: '充值成功',
        withdrawSuccess: '提现申请已提交',
        insufficientBalance: '余额不足'
      },
      // Profile
      profile: {
        gameHistory: '对局历史',
        won: '胜',
        draw: '和',
        lost: '负',
        title: '个人资料',
        rating: '积分',
        gamesPlayed: '对局数',
        gamesWon: '胜场',
        winRate: '胜率',
        memberSince: '注册时间',
        settings: '设置',
        language: '语言',
        kyc: {
          title: '身份验证',
          notStarted: '未验证',
          pending: '验证中',
          approved: '已验证',
          required: '需要验证',
          startVerification: '开始验证',
          description: '首次提现前需要完成身份验证'
        },
        botStats: {
          title: '人机统计',
          byDifficulty: '按难度',
          totalGames: '总对局',
          wins: '胜',
          losses: '负',
          draws: '和',
          winRate: '胜率',
          currentStreak: '当前连胜',
          bestStreak: '最佳连胜',
          recentGames: '最近人机对局',
          noGames: '暂无人机对局',
          playNow: '立即开始'
        }
      },
      // Common
      common: {
        loading: '加载中...',
        error: '错误',
        success: '成功',
        cancel: '取消',
        confirm: '确认',
        save: '保存',
        delete: '删除',
        edit: '编辑',
        back: '返回',
        share: '分享',
        next: '下一步',
        submit: '提交',
        search: '搜索',
        filter: '筛选',
        noResults: '无结果',
        minutes: '分钟',
        seconds: '秒',
        you: '你',
        view: '查看',
        play: '开始'
      },
      // Achievements
      achievements: {
        title: '成就',
        description: '通过对局和提升技能解锁成就',
        points: '积分',
        unlocked: '已解锁',
        progress: '进度',
        unlockedTitle: '已解锁',
        lockedTitle: '未解锁',
        noAchievements: '该分类暂无成就',
        viewAll: '查看全部成就',
        categories: {
          all: '全部',
          bot: '人机',
          pvp: '对战',
          rating: '积分',
          special: '特殊',
          account: '账户'
        }
      },
      // Friends
      friends: {
        title: '好友',
        description: '添加好友并向他们发起挑战',
        addFriend: '添加好友',
        searchPlaceholder: '输入用户名...',
        myFriends: '我的好友',
        requests: '请求',
        challenges: '挑战',
        noFriends: '暂无好友',
        noIncoming: '暂无收到的请求',
        noOutgoing: '暂无发出的请求',
        noChallenges: '暂无挑战',
        incoming: '收到的',
        outgoing: '发出的',
        incomingChallenges: '收到的挑战',
        outgoingChallenges: '发出的挑战',
        wantsToBeYourFriend: '想添加你为好友',
        pendingRequest: '请求待处理',
        waiting: '等待中...',
        challenge: '挑战',
        accept: '接受',
        decline: '拒绝',
        removed: '好友已删除',
        challengePlayer: '挑战玩家',
        challengeDescription: '向 {{name}} 发起挑战',
        sendChallenge: '发送挑战',
        stakeNote: '设为0为免费对局。有赌注需要身份验证。'
      },
      // Settings
      settings: {
        title: '棋盘设置',
        description: '自定义棋盘外观',
        boardTheme: '棋盘主题',
        pieceStyle: '棋子样式',
        preview: '预览'
      },
      // KYC
      kyc: {
        mockMode: 'Sumsub未配置，使用模拟验证。',
        submitted: '验证已提交，我们将审核您的文件。',
        resubmitted: '文件已重新提交审核。',
        approved: '验证通过！现在可以进行真金对战。',
        rejected: '验证被拒绝，请重试。',
        retry: '需要补充文件',
        error: '验证错误，请重试。',
        verifiedTitle: '身份已验证',
        verifiedDescription: '您可以进行真金对战。',
        pendingTitle: '验证处理中',
        pendingDescription: '您的文件正在审核，通常需要几分钟。',
        rejectedTitle: '验证未通过',
        rejectedDescription: '请使用有效证件重试。',
        retryTitle: '需要补充信息',
        retryVerification: '重新验证',
        approveMock: '通过（测试模式）',
        whyTitle: '为什么需要验证？',
        whyText: '对局和充值无需验证。为了将奖金提现到银行卡，我们需要一次性确认您的身份 — 约需 2–3 分钟。',
        chooseMethod: '选择验证方式',
        diditTitle: '护照或身份证 + 自拍',
        diditDesc: '拍摄证件并自拍。支持任何国家。通常一分钟内出结果。',
        mtsTitle: '通过 MTS ID',
        mtsDesc: '使用 MTS 账户登录 — 姓名、出生日期和电话由运营商确认。适用于俄罗斯居民。',
        start: '开始',
        notConfigured: '暂不可用',
        testMode: '测试模式：尚未连接服务商',
        verifiedVia: '验证方式',
        returned: '正在检查结果…',
        cancelled: '验证已取消。您可以重试。',
        incomplete: 'MTS ID 未返回姓名。请使用证件验证。',
        tryAgain: '重试'
      },
      // Notifications
      notifications: {
        kycApproved: '身份验证已通过！',
        kycRejected: '身份验证被拒绝。',
        newChallenge: '收到新的对局邀请！',
        challengeAccepted: '挑战已接受！对局即将开始...'
      }
    }
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('language') || 'en',
    fallbackLng: 'en',
    supportedLngs: ['en', 'ru', 'zh'],
    interpolation: {
      escapeValue: false
    }
  });

// Function to detect language from Telegram
export const detectLanguage = (telegramLangCode) => {
  if (!telegramLangCode) return 'en';
  
  // Map Telegram language codes to our supported languages
  const langMap = {
    'en': 'en',
    'ru': 'ru',
    'zh': 'zh',
    'zh-hans': 'zh',
    'zh-hant': 'zh',
    'zh-cn': 'zh',
    'zh-tw': 'zh'
  };
  
  const code = telegramLangCode.toLowerCase();
  return langMap[code] || 'en'; // Default to English
};

export default i18n;
